import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useVariantLensApiClient } from '../features/auth/hooks/useVariantLensApiClient'
import IgvMount, { type IgvReference, type IgvTrack } from '../features/viewers/igv/IgvMount'
import { DEFAULT_IGV_SETTINGS, toIgvTrack, type ApiTrack } from './igvTracks'

/**
 * Tracks-only benchmarking-sample viewer (GEN-5481).
 *
 * Opens a set sample via GET /benchmarks/{set}/{sample}/tracks, which returns
 * IGV tracks plus an optional reference. Same shape as OrderViewerPage: the
 * benchmarking path yields alignment/variant tracks but no case-scoped variant
 * table, so this renders the IGV view only.
 */

type ApiReference = {
  id: string
  fastaUrl: string
  indexUrl: string
}

type BenchmarkTracksResponse = {
  set_id?: string
  sample_dir?: string
  sample_id?: string
  tracks?: ApiTrack[]
  reference?: ApiReference | null
}

// Same custom-reference labels as OrderViewerPage; both builds are GRCh38 and
// the LSMC CRAMs use the no-alt analysis set (GEN-5234).
function referenceDisplayName(id: string): string {
  switch (id) {
    case 'hg38_no_alt':
      return 'GRCh38 (no-alt)'
    case 'hg38_broad':
      return 'GRCh38 (Broad)'
    default:
      return 'GRCh38'
  }
}

// A minimum interval between presign refreshes, so a genuinely broken URL can't
// spin the fetch effect. Mirrors OrderViewerPage.
const REFRESH_MIN_INTERVAL_MS = 30_000

// Match only genuine presign-expiry signals (S3 403 / Forbidden / Access
// Denied), identical to OrderViewerPage, so an unrelated igv.js error cannot
// trigger a spurious presign refresh.
function looksLikeExpiredUrl(message: string): boolean {
  return /\b(403|forbidden|access denied)\b/i.test(message)
}

const BenchmarkViewerPage: React.FC = () => {
  const { setId: rawSetId, sampleDir: rawSampleDir } = useParams<{ setId: string; sampleDir: string }>()
  const setId = rawSetId ? decodeURIComponent(rawSetId) : ''
  const sampleDir = rawSampleDir ? decodeURIComponent(rawSampleDir) : ''
  const apiClient = useVariantLensApiClient()

  const [loading, setLoading] = useState(true)
  const [tracks, setTracks] = useState<ApiTrack[] | null>(null)
  const [reference, setReference] = useState<ApiReference | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [refreshNonce, setRefreshNonce] = useState(0)
  const lastRefreshRef = useRef(0)
  // The sample the current tracks are loaded for; claimed synchronously so a
  // bumped refreshNonce only means "refresh in place" when the sample is
  // unchanged. Mirrors OrderViewerPage.tokenRef.
  const sampleKeyRef = useRef<string | null>(null)

  const refresh = useCallback(() => {
    const now = Date.now()
    if (now - lastRefreshRef.current < REFRESH_MIN_INTERVAL_MS) return
    lastRefreshRef.current = now
    setRefreshNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!setId || !sampleDir) {
      setLoading(false)
      setError('No sample specified')
      return
    }
    const sampleKey = `${setId}/${sampleDir}`
    const abort = new AbortController()
    const isRefresh = refreshNonce > 0 && sampleKeyRef.current === sampleKey
    if (!isRefresh) {
      sampleKeyRef.current = sampleKey
      setLoading(true)
      setTracks(null)
      setReference(null)
    }
    setError(null)
    const url = `/benchmarks/${encodeURIComponent(setId)}/${encodeURIComponent(sampleDir)}/tracks`
    apiClient
      .get(url, undefined, true, abort.signal)
      .then(async (response) => {
        if (response.status === 404) throw new Error('Sample not found in this benchmarking set')
        if (response.status === 503) throw new Error('Benchmark listing is temporarily unavailable')
        if (!response.ok) throw new Error(`API returned ${response.status}`)
        const body = (await response.json()) as BenchmarkTracksResponse
        setTracks(body.tracks ?? [])
        setReference(body.reference ?? null)
      })
      .catch((e: unknown) => {
        if (abort.signal.aborted) return
        if (!isRefresh) setError(e instanceof Error ? e.message : 'Failed to load sample')
        else lastRefreshRef.current = 0
      })
      .finally(() => {
        if (!abort.signal.aborted && !isRefresh) setLoading(false)
      })
    return () => abort.abort()
  }, [setId, sampleDir, apiClient, refreshNonce])

  const igvTracks = useMemo<IgvTrack[] | undefined>(
    () => (tracks ? tracks.map((t) => toIgvTrack(t, DEFAULT_IGV_SETTINGS)) : undefined),
    [tracks]
  )
  const igvReference = useMemo<IgvReference | undefined>(
    () =>
      reference
        ? {
            id: reference.id,
            name: referenceDisplayName(reference.id),
            fastaURL: reference.fastaUrl,
            indexURL: reference.indexUrl,
          }
        : undefined,
    [reference]
  )

  const onIgvError = useCallback(
    (message: string) => {
      if (looksLikeExpiredUrl(message)) refresh()
    },
    [refresh]
  )

  return (
    <div className="flex h-full flex-col">
      <header className="border-border-subtle flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-text-heading font-mono text-sm">{sampleDir}</span>
          <span className="text-text-subtle text-xs">Benchmark sample ({setId})</span>
        </div>
        <Link to="/home" className="text-brand-700 hover:text-brand-800 text-sm underline underline-offset-2">
          Back to samples
        </Link>
      </header>

      <div className="min-h-0 flex-1">
        {loading && (
          <p data-testid="benchmark-viewer-loading" className="text-text-subtle p-6 text-sm">
            Loading sample...
          </p>
        )}
        {!loading && error && (
          <p data-testid="benchmark-viewer-error" role="alert" className="text-error-600 p-6 text-sm">
            {error}
          </p>
        )}
        {!loading && !error && igvTracks && igvTracks.length === 0 && (
          <p data-testid="benchmark-viewer-empty" className="text-text-subtle p-6 text-sm">
            This sample has no viewable tracks.
          </p>
        )}
        {!loading && !error && igvTracks && igvTracks.length > 0 && (
          <IgvMount
            tracks={igvTracks}
            genome="hg38"
            reference={igvReference}
            onError={onIgvError}
            reloadKey={`${setId}/${sampleDir}`}
            data-testid="benchmark-viewer-igv"
            className="h-full"
            hostClassName="min-h-full"
          />
        )}
      </div>
    </div>
  )
}

export default BenchmarkViewerPage
